import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pause, Play, Maximize2, X } from 'lucide-react';
import MediaImage from './MediaImage';
import YouTubeCoverFrame, { youtubeCoverContainerStyle } from './YouTubeCoverFrame';

type LazyYouTubeEmbedProps = {
  videoId: string;
  title: string;
  poster: string;
  posterAlt?: string;
  posterTitle?: string;
};

function buildEmbedSrc(videoId: string, withControls = false) {
  const base = `https://www.youtube-nocookie.com/embed/${videoId}`;
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    loop: '1',
    playlist: videoId,
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    enablejsapi: '1',
    controls: withControls ? '1' : '0',
  });
  return `${base}?${params.toString()}`;
}

export default function LazyYouTubeEmbed({
  videoId,
  title,
  poster,
  posterAlt = 'Action Game Caen Gameside vidéo présentation',
  posterTitle = 'Action Game Caen Gameside vidéo',
}: LazyYouTubeEmbedProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const indicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (shouldLoad || !rootRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '420px 0px' },
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    return () => {
      if (indicatorTimerRef.current) clearTimeout(indicatorTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen]);

  function togglePause(event: React.MouseEvent) {
    event.stopPropagation();
    if (!iframeRef.current?.contentWindow) return;
    const nextPaused = !paused;
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: nextPaused ? 'pauseVideo' : 'playVideo', args: [] }),
      '*',
    );
    setPaused(nextPaused);
    setShowIndicator(true);
    if (indicatorTimerRef.current) clearTimeout(indicatorTimerRef.current);
    indicatorTimerRef.current = setTimeout(() => setShowIndicator(false), 900);
  }

  function openFullscreen(event: React.MouseEvent | React.TouchEvent) {
    event.stopPropagation();
    setIsFullscreen(true);
  }

  const inlineSrc = buildEmbedSrc(videoId, false);
  const fullscreenSrc = buildEmbedSrc(videoId, true);

  return (
    <>
      <div ref={rootRef} className="group absolute inset-0 bg-black overflow-hidden" style={youtubeCoverContainerStyle}>
        <MediaImage
          src={poster}
          alt={posterAlt}
          title={posterTitle}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${shouldLoad ? 'opacity-0' : 'opacity-80'}`}
        />
        {shouldLoad && (
          <YouTubeCoverFrame
            ref={iframeRef}
            src={inlineSrc}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}
        {shouldLoad && (
          <button
            type="button"
            onClick={togglePause}
            className="absolute inset-0 z-20 cursor-pointer bg-transparent"
            aria-label={paused ? 'Reprendre la vidéo' : 'Mettre en pause'}
          >
            <span className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none ${showIndicator ? 'opacity-100' : 'opacity-0'}`}>
              <span className="bg-black/55 rounded-full p-4 flex items-center justify-center">
                {paused ? <Play size={28} className="text-white" /> : <Pause size={28} className="text-white" />}
              </span>
            </span>
          </button>
        )}
        {shouldLoad && (
          <button
            type="button"
            onClick={openFullscreen}
            aria-label="Ouvrir la vidéo en plein écran"
            className="absolute top-3 right-3 z-30 inline-flex items-center justify-center h-11 w-11 rounded-full bg-black/55 text-white backdrop-blur-sm shadow-lg transition-all duration-200 hover:bg-black/80 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-yellow-400 lg:opacity-0 lg:group-hover:opacity-100 lg:focus:opacity-100"
          >
            <Maximize2 size={18} strokeWidth={2.5} />
          </button>
        )}
      </div>

      {isFullscreen && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={`Vidéo plein écran : ${title}`}
        >
          <button
            type="button"
            className="absolute inset-0"
            onClick={() => setIsFullscreen(false)}
            aria-label="Fermer la vidéo"
          />
          <div className="relative w-full max-w-6xl">
            <button
              type="button"
              onClick={() => setIsFullscreen(false)}
              aria-label="Fermer la vidéo"
              className="absolute -top-12 right-0 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/30 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
            <div className="relative w-full overflow-hidden rounded-2xl bg-black shadow-[0_30px_80px_rgba(0,0,0,0.6)]" style={{ aspectRatio: '16 / 9' }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={fullscreenSrc}
                title={`${title} — plein écran`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
